import { Radio } from '@mui/material';
import { DEFAULT_DATE_FORMAT_FNS, FilterKeys, SchoolYear } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  GroupSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { toggleArrayProperty } from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { format } from 'date-fns';
import isEqual from 'lodash.isequal';
import React, { FC, useCallback, useRef } from 'react';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { FormRadioStyled } from '../../../../../components/uikit-components/FormCheckbox/FormRadio.styled';
import { FormRadioGroup } from '../../../../../components/uikit-components/FormCheckbox/FormRadioGroup';
import { MarkbookFilterForm } from '../../../../../context/markbook/MarkbookFilterContext';
import { useSchool } from '../../../../../hooks/useSchool';
import { useConfirmAction } from '../helpers';
import { useValidateGroups } from '../helpers';
import { StepWrapper } from '../MarkbookFilterModal';

const groupOptions = [
  {
    labelTextId: 'assessments-markbook-byAgeGroup',
    value: FilterKeys.AgeGroup,
  },
  {
    labelTextId: 'assessments-markbook-byGroup',
    value: FilterKeys.Group,
  },
];

type SelectStudentsStepProps = {
  defaultDates: [string, string];
  defaultValidity?: SchoolYear;
};

export const SelectStudentsStep: FC<SelectStudentsStepProps> = ({
  defaultDates,
  defaultValidity,
}) => {
  const { watch, control, setValue } = useFormContext<MarkbookFilterForm>();
  const groups = watch(FilterKeys.Group);
  const ageGroups = watch(FilterKeys.AgeGroup);
  const selectedGroupType = watch('group_type');
  const [dateFrom, dateTo] = watch(FilterKeys.Date);
  const { schoolId = '' } = useSchool();
  const { formatMessage } = useIntl();
  const selectedAssessmentIds = watch('selectedAssessmentIds');

  const hasAssessmentSelections = selectedAssessmentIds === 'all' || selectedAssessmentIds.size > 0;

  const schoolPeriodsRangeSelectRef = useRef<SchoolPeriodsRangeSelectComponent>(null);

  const withConfirmation = useConfirmAction({
    shouldConfirm: hasAssessmentSelections,
    afterConfirm: () => {
      setValue('selectedAssessmentIds', new Set());
    },
  });
  const { validateGroups, isLoading } = useValidateGroups();

  const resetDates = useCallback(() => {
    schoolPeriodsRangeSelectRef.current?.updateDates([dateFrom, dateTo]);
  }, [dateFrom, dateTo]);

  const clearAssessmentSelections = useCallback(() => {
    if (hasAssessmentSelections) {
      setValue('selectedAssessmentIds', new Set());
    }
  }, [hasAssessmentSelections, setValue]);

  const handleDateChange =
    (field: ControllerRenderProps<MarkbookFilterForm, FilterKeys.Date>) =>
    async ([start, end]: [Date, Date]) => {
      const startDate = format(start, DEFAULT_DATE_FORMAT_FNS);
      const endDate = format(end, DEFAULT_DATE_FORMAT_FNS);

      if (isEqual(field.value, [startDate, endDate])) return;

      const shouldValidateGroups = selectedGroupType === FilterKeys.Group && groups.length > 0;

      let groupValidation = { removedGroups: [] as string[], remainingGroups: [] as any[] };
      if (shouldValidateGroups) {
        groupValidation = await validateGroups([startDate, endDate]);
      }

      const needsConfirmation = groupValidation.removedGroups.length > 0;

      await withConfirmation(
        () => {
          field.onChange([startDate, endDate]);
          if (groupValidation.removedGroups.length > 0) {
            setValue(
              FilterKeys.Group,
              groups.filter((group) => !groupValidation.removedGroups.includes(group)),
            );
          }
        },
        resetDates,
        needsConfirmation,
      );
    };

  const handleGroupTypeChange = useCallback(
    async (field: { onChange: (value: any) => void }, value: string) => {
      await withConfirmation(() => {
        field.onChange(value);
      });
    },
    [withConfirmation],
  );

  const handleGroupSelection = useCallback(
    async (field: ControllerRenderProps<MarkbookFilterForm, 'group'>, groupId: string) => {
      const isRemoving = groups.includes(groupId);

      if (isRemoving) {
        await withConfirmation(() => {
          field.onChange(toggleArrayProperty(field.value, groupId));
        });
      } else field.onChange(toggleArrayProperty(field.value, groupId));
    },
    [groups, withConfirmation],
  );

  const handleAgeGroupSelection = useCallback(
    async (
      field: ControllerRenderProps<MarkbookFilterForm, FilterKeys.AgeGroup>,
      newValue: string[],
    ) => {
      const isRemoving = ageGroups.some((id) => !newValue.includes(id));

      const appliedChanges = toggleMultipleValueArrayProperty(field.value, newValue);

      if (isRemoving) {
        await withConfirmation(() => {
          field.onChange(appliedChanges);
        });
      } else field.onChange(appliedChanges);
    },
    [ageGroups, withConfirmation],
  );

  const handleAgeGroupClear = useCallback(
    async (field: ControllerRenderProps<MarkbookFilterForm, FilterKeys.AgeGroup>) => {
      await withConfirmation(() => {
        field.onChange([]);
      });
    },
    [withConfirmation],
  );

  return (
    <StepWrapper px={5} gap={3}>
      <Controller
        control={control}
        name={FilterKeys.Date}
        rules={{ required: true }}
        render={({ field }) => (
          <SchoolPeriodsRangeSelect
            schoolId={schoolId ?? ''}
            isLoading={isLoading}
            date={field.value.length > 0 ? field.value : defaultDates}
            ref={schoolPeriodsRangeSelectRef}
            onSetDate={handleDateChange(field)}
            hasManagePermission
            showPeriods
            defaultSchoolYear={defaultValidity}
          />
        )}
      />

      <FormRadioGroup
        options={groupOptions}
        name="group_type"
        listView={false}
        disabled={isLoading}
        renderOption={(option, _, field) => {
          return (
            <FormRadioStyled
              key={`${option.value}`}
              label={
                option.labelTextId ? <FormattedMessage id={option.labelTextId} /> : option.label
              }
              control={
                <Radio
                  checked={`${option.value}` === String(field.value)}
                  onChange={() => handleGroupTypeChange(field, option.value)}
                />
              }
            />
          );
        }}
      />

      {selectedGroupType === 'group' && (
        <Controller
          control={control}
          name="group"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <GroupSelectMultiple
              schoolId={schoolId}
              onError={(id) => {
                field.onChange(toggleArrayProperty(field.value, id));
                clearAssessmentSelections();
              }}
              isLoading={isLoading}
              onSelectGroupId={(groupId) => handleGroupSelection(field, groupId)}
              placeholder={formatMessage({ id: 'assessments-Group' })}
              selectedIds={field.value || []}
              error={fieldState.error}
              filters={{ [FilterKeys.IntersectDate]: [dateFrom, dateTo] }}
            />
          )}
        />
      )}

      {selectedGroupType === FilterKeys.AgeGroup && (
        <Controller
          control={control}
          name={FilterKeys.AgeGroup}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <AgeGroupSelectMultiple
                schoolId={schoolId}
                onClear={() => handleAgeGroupClear(field)}
                placeholder={formatMessage({ id: 'schoolProperty-AgeGroup' })}
                selectedIds={field.value}
                error={fieldState.error}
                onSelect={(newValue) => handleAgeGroupSelection(field, newValue)}
                userRole={SchoolUserRole.Student}
                allowArchived
              />
            );
          }}
        />
      )}
    </StepWrapper>
  );
};
